import React from "react";
import { DEFAULT_SEPARATOR } from "../constants";

export const isPromise = promise => !!promise && typeof promise.then === "function";

export const defaultParsePaste = str => (isEmpty(str) ? [] : str.split(/\r\n|\n|\r/).map(row => row.split("\t")));

export const isEmpty = value =>
  typeof value === "string"
    ? value.trim() === ""
    : typeof value === "object"
    ? value === null || Object.keys(value).length === 0
    : Array.isArray(value)
    ? value.length === 0
    : value === undefined;

export const isTrue = value => (value && typeof value === "string" ? value === "true" : value === true);

export const isFalse = value => !isTrue(value);

export const bytesToMB = value => (value ? value / (1000 * 1000) : 0);

export const getStyleColor = (style, color) => {
  return `${style}${DEFAULT_SEPARATOR}${color}`;
};

//** Encode Size Value when used as key in MuiTable **//
export const sizeKey = size => `${size}`.replaceAll(".", "_");

export const inputOptions = sm => ({
  sm,
  xs: 12,
  fullWidth: true,
  options: { fullWidth: true }
});

export const fieldOptions = sm => ({
  sm,
  xs: 12,
  fullWidth: true
});

export const humanizeNumber = number => {
  return number ? number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : number;
};

export const getDistinctValues = values => {
  if (!values) return values;
  if (!Array.isArray(values)) return values;
  let set = new Set();
  values.forEach(value => set.add(value));
  return [...set];
};

export const getDistinctObjectValues = (list, idKey = "id") => {
  if (isEmpty(list)) return [];
  const data = list.reduce((acc, e) => ({ ...acc, [e[idKey]]: e }), {});
  return Object.values(data);
};

export const toFixed = (value, precision = 2) => {
  if (typeof value !== "number") return value;
  return Number(value.toFixed(precision));
};

export const toFixedValue = (value, precision = 2) => {
  if (typeof value !== "number") return value;
  return Number(value.toFixed(precision));
};

export const signedValue = value => {
  if (typeof value !== "number") return value;
  const sign = value > 0 ? "+" : "";
  return `${sign}${value}`;
};

export const toFixedSigned = (value, precision = 2) => {
  if (typeof value !== "number") return value;
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(precision)}`;
};

export const listFromCsv = csv => {
  return typeof csv !== "string"
    ? []
    : csv
        .split(",")
        .map(e => e.trim())
        .filter(e => !isEmpty(e));
};

export const mapFromCsv = (csv, delimitter = ",") => {
  let result = {};
  if (csv) {
    const array1 = csv.split(delimitter);
    array1.forEach(e => {
      let array2 = e.split("=");
      if (array2.length === 2) {
        result[array2[0]] = array2[1].trim();
      }
    });
  }
  return result;
};

// data is Array of object: [{key: , weight: }]
export const getWeightedMean = data => {
  if (isEmpty(data)) return 0;
  const weightedSum = data.reduce((acc, el) => acc + el.key * el.weight, 0);
  const totalWeight = data.reduce((acc, el) => acc + el.weight, 0);
  return totalWeight > 0 ? weightedSum / totalWeight : 0;
};

export const numberWithCommas = x => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// /// Callbacks for Sorting ///
export const cbChecklistItemSort = (a, b) => {
  if (a.similarCount === b.similarCount) {
    if (a.sortField === b.sortField) {
      if (a.itemNo === b.itemNo) {
        return a.styleName ? -1 : 1;
      } else {
        if (a.parentRepeat === b.parentRepeat) {
          return a.itemNo - b.itemNo;
        } else {
          return a.parentRepeat < b.parentRepeat ? -1 : 1;
        }
      }
    } else {
      return a.sortField < b.sortField ? -1 : 1;
    }
  } else {
    return b.similarCount - a.similarCount;
  }
};

export const partialData = (jsonStr, onView) => {
  if (jsonStr && jsonStr.length > 50) {
    return (
      <div>
        {jsonStr.substring(0, 60)}
        {jsonStr.length > 60 ? " ... " : ""}
        {jsonStr.length > 60 && (
          <span style={{ color: "blue" }} onClick={onView}>
            view
          </span>
        )}
      </div>
    );
  }
  return jsonStr;
};

export const syntaxHighlight = (json = "") => {
  json = json.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g, match => {
    var color = "darkorange"; //number
    if (/^"/.test(match)) {
      if (/:$/.test(match)) {
        color = "red"; // key
      } else {
        color = "green"; //string
      }
    } else if (/true|false/.test(match)) {
      color = "blue"; // boolean
    } else if (/null/.test(match)) {
      color = "magenta"; //null
    }
    return `<span style="color: ${color}" > ${match} </span>`;
  });
};

export const truncate = (str, length = 20) => {
  if (!str || typeof str !== "string" || str.length <= length) return str;
  if (str.length > length) return `${str.substring(0, length - 3)}...`;
};

export const multiLineText = (text, length, delimitter = " ") => {
  if (!text || typeof text !== "string") return [];

  const result = [];
  while (text.length > length) {
    let idx = text.substring(0, length).lastIndexOf(delimitter);
    if (idx === -1) {
      idx = text.indexOf(delimitter);
      if (idx === -1) break;
    }
    result.push(text.substring(0, idx));
    text = text.substring(idx + 1);
  }
  result.push(text);

  return result;
};

export const strictMultiLineText = (text, length) => {
  if (!text || typeof text !== "string") return [];

  const result = [];
  while (text.length > length) {
    result.push(text.substring(0, length));
    text = text.substring(length);
  }
  result.push(text);

  return result;
};

export const getTrimmedCSV = values => {
  if (!values) return;
  let result = null;
  if (typeof values === "string") {
    result = values
      .split(",")
      .filter(e => e.trim())
      .map(e => e.trim())
      .join(",");
  } else if (Array.isArray(values)) {
    result = values
      .filter(e => e.trim())
      .map(e => e.trim())
      .join(",");
  }
  return result;
};

export const getTrimmedList = values => {
  if (!values) return;
  let result = null;
  if (typeof values === "string") {
    result = values
      .split(",")
      .filter(e => e.trim())
      .map(e => e.trim());
  } else if (Array.isArray(values)) {
    result = values.filter(e => e.trim()).map(e => e.trim());
  }
  return result;
};

export const getSetting = (settings, key, resource) => {
  if (!isEmpty(settings) && !isEmpty(key)) {
    return settings.find(e => e.key === key && (!resource || e.resource === resource))?.value;
  }
};

export const findDuplicates = arr => {
  let duplicates = arr.filter((item, index) => arr.indexOf(item) !== index);
  return [...new Set(duplicates)]; // Unique duplicates
};

export const findMinValue = data => {
  if (!data || !Array.isArray(data)) return;
  let minValue = null;
  for (let i = 0; i < data.length; i++) {
    if (minValue == null || parseFloat(data[i]) < minValue) {
      minValue = data[i];
    }
  }
  return minValue;
};

export const findMaxValue = data => {
  if (!data || !Array.isArray(data)) return;
  let maxValue = null;
  for (let i = 0; i < data.length; i++) {
    if (maxValue == null || parseFloat(data[i]) > maxValue) {
      maxValue = data[i];
    }
  }
  return maxValue;
};

export const safeatob = str => Buffer.from(str, "base64").toString("binary");
export const safebtoa = str => new Buffer.from(str, "binary").toString("base64");
